import { useCallback, useMemo } from "react";
import { useTaskBoardsQuery } from "./useTaskBoardsQuery";

export interface BoardRoleFlags {
  isBoardAdmin: boolean;
  isBoardMember: boolean;
  isBoardViewer: boolean;
}

interface UseBoardRolesFactoryOptions {
  entityId: string;
  meId: string | undefined;
}

const DEFAULT_BOARD_ROLE_FLAGS: BoardRoleFlags = {
  isBoardAdmin: false,
  isBoardMember: false,
  isBoardViewer: false,
};

export const useBoardRolesFactory = ({
  entityId,
  meId,
}: UseBoardRolesFactoryOptions) => {
  const { getBoardByIdFromCache } = useTaskBoardsQuery({
    entityId,
  });

  const getBoardRoles = useCallback(
    (boardId: string | null) => {
      const roles = { ...DEFAULT_BOARD_ROLE_FLAGS };

      if (!boardId || !meId) {
        return roles;
      }

      const matchingBoard = getBoardByIdFromCache(boardId);

      if (!matchingBoard) {
        return roles;
      }

      matchingBoard.users.forEach((user) => {
        if (user.id === meId) {
          if (user.type === "owner") {
            roles.isBoardAdmin = true;
          } else if (user.type === "member") {
            roles.isBoardMember = true;
          } else if (user.type === "viewer") {
            roles.isBoardViewer = true;
          }
        }
      });

      return roles;
    },
    [getBoardByIdFromCache, meId]
  );

  return getBoardRoles;
};

interface UseBoardRolesOptions extends UseBoardRolesFactoryOptions {
  boardId: string | null;
}

export const useBoardRoles = ({
  boardId,
  entityId,
  meId,
}: UseBoardRolesOptions): BoardRoleFlags => {
  const getBoardRoles = useBoardRolesFactory({
    entityId,
    meId,
  });

  const boardRoles = useMemo(
    () => getBoardRoles(boardId),
    [getBoardRoles, boardId]
  );

  return boardRoles;
};
